import React, { useState, useEffect } from 'react';
import { BookOpen, Shield, ChevronDown, ChevronRight, Copy, Terminal,
  Webhook, ExternalLink, ArrowLeft, Globe } from 'lucide-react';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DeveloperPortalPage() {
  const [docs, setDocs] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/v1/docs`).then(r => r.json()).then(setDocs).catch(console.error);
  }, []);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
  };

  if (!docs) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">H</div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">HireFast AI — Developer Docs</h1>
              <p className="text-xs text-gray-500">API v{docs.version} — Integration Reference for Partners</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/developers/changelog" className="text-xs text-purple-600 hover:text-purple-800 font-medium">
              Changelog
            </a>
            <a href="/login" className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
              <ArrowLeft className="h-3 w-3" /> Back to Login
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Overview */}
        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Overview</h2>
          <p className="text-sm text-gray-600 leading-relaxed">
            The HireFast AI API allows ATS platforms like Ceipal to embed AI-powered recruitment intelligence directly into their workflows.
            For every job, access JD Analysis, Market Intelligence, Discovery Call Questions, Smart Sourcing, AI Interview links, and public share URLs — all via a simple REST API.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">REST API</span>
            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">JSON Responses</span>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Webhook Callbacks</span>
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">Region-Scoped</span>
          </div>
        </section>

        {/* Auth */}
        <section className="bg-white border rounded-2xl p-6">
          <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Shield className="h-5 w-5 text-indigo-600" /> Authentication
          </h2>
          <p className="text-sm text-gray-600 mb-3">{docs.authentication.description}</p>
          <div className="bg-gray-900 rounded-lg px-4 py-3 flex items-center gap-2">
            <code className="text-sm text-green-300 font-mono flex-1">{docs.authentication.example}</code>
            <button onClick={() => copy(docs.authentication.example)} className="text-gray-400 hover:text-white"><Copy className="h-4 w-4" /></button>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-600">Rate Limit</p>
              <p className="text-sm text-gray-800">{docs.rate_limiting.limit}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-600">Key Generation</p>
              <p className="text-sm text-gray-800">Admin only — from HireFast Settings</p>
            </div>
          </div>
        </section>

        {/* Versioning */}
        {docs.versioning && (
          <section className="bg-white border rounded-2xl p-6">
            <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Globe className="h-5 w-5 text-purple-600" /> API Versioning
            </h2>
            <p className="text-sm text-gray-600 mb-3">{docs.versioning.description}</p>
            <div className="bg-gray-900 rounded-lg px-4 py-3 flex items-center gap-2 mb-3">
              <code className="text-sm text-green-300 font-mono flex-1">{docs.versioning.example}</code>
              <button onClick={() => copy(docs.versioning.example)} className="text-gray-400 hover:text-white"><Copy className="h-4 w-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-purple-50 rounded-lg p-3">
                <p className="text-xs font-semibold text-purple-700">Current Version</p>
                <p className="text-sm font-mono text-purple-900">{docs.versioning.current_version}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-600">Supported</p>
                <p className="text-sm font-mono text-gray-800">{docs.versioning.supported_versions?.join(', ')}</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 mb-3">
              <p className="text-xs font-semibold text-gray-600 mb-1">Response Headers</p>
              {docs.versioning.response_headers && Object.entries(docs.versioning.response_headers).map(([k, v]) => (
                <div key={k} className="flex gap-2 text-xs text-gray-600 mb-0.5">
                  <code className="font-mono text-purple-600 shrink-0">{k}</code>
                  <span>{v}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 italic">{docs.versioning.policy}</p>
          </section>
        )}

        {/* Getting Started */}
        {docs.getting_started && (
          <section className="bg-white border rounded-2xl p-6">
            <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-emerald-600" /> Getting Started
            </h2>
            <div className="space-y-2">
              {docs.getting_started.steps?.map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                  <p className="text-sm text-gray-700">{step.replace(/^\d+\.\s*/, '')}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-amber-800 mb-1">Security</p>
              {docs.getting_started.security_notes?.map((note, i) => (
                <p key={i} className="text-xs text-amber-700">{note}</p>
              ))}
            </div>
          </section>
        )}

        {/* Base URL */}
        <section className="bg-white border rounded-2xl p-6">
          <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-600" /> Base URL
          </h2>
          <div className="bg-gray-900 rounded-lg px-4 py-3 flex items-center gap-2">
            <code className="text-sm text-green-300 font-mono flex-1">{docs.base_url}</code>
            <button onClick={() => copy(docs.base_url)} className="text-gray-400 hover:text-white"><Copy className="h-4 w-4" /></button>
          </div>
        </section>

        {/* Sandbox Mode */}
        {docs.sandbox && (
          <section className="bg-white border-2 border-orange-200 rounded-2xl p-6">
            <h2 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-orange-100 text-orange-700">SANDBOX</span>
              Test Without Affecting Live Data
            </h2>
            <p className="text-sm text-gray-600 mb-4">{docs.sandbox.description}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">How to Use</p>
                {docs.sandbox.how_to_use?.map((step, i) => (
                  <div key={i} className="flex items-start gap-2 mb-1">
                    <span className="w-4 h-4 rounded-full bg-orange-100 text-orange-700 text-[9px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                    <p className="text-xs text-gray-600">{step}</p>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">What's Different in Sandbox</p>
                {docs.sandbox.differences && Object.entries(docs.sandbox.differences).map(([k, v]) => (
                  <div key={k} className="flex items-start gap-2 mb-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-1.5 shrink-0" />
                    <p className="text-xs text-gray-600">{v}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Endpoints */}
        <section>
          <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Terminal className="h-5 w-5 text-purple-600" /> API Endpoints
          </h2>
          <div className="space-y-2">
            {docs.endpoints.map((ep, i) => {
              const isExpanded = expanded === `ep_${i}`;
              const methodColor = ep.method === 'GET' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700';
              return (
                <div key={i} className="bg-white border rounded-xl overflow-hidden">
                  <button onClick={() => setExpanded(isExpanded ? null : `ep_${i}`)}
                    className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-gray-50 transition-colors">
                    <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded ${methodColor} shrink-0`}>{ep.method}</span>
                    <code className="text-sm font-mono text-gray-900 flex-1">{ep.path}</code>
                    {ep.auth_required && <Shield className="h-3.5 w-3.5 text-amber-500" />}
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                  </button>
                  {isExpanded && (
                    <div className="border-t px-5 py-4 bg-gray-50/50 space-y-4">
                      <p className="text-sm text-gray-700">{ep.description}</p>
                      {ep.request_body && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">Request Body</p>
                          <pre className="bg-white border rounded-lg p-3 text-xs font-mono text-gray-700 overflow-x-auto">{JSON.stringify(ep.request_body, null, 2)}</pre>
                        </div>
                      )}
                      {ep.params && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">Query Parameters</p>
                          {Object.entries(ep.params).map(([k, v]) => (
                            <p key={k} className="text-xs text-gray-600"><code className="font-mono text-indigo-600">{k}</code>: {v}</p>
                          ))}
                        </div>
                      )}
                      {ep.response_example && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">Response Example</p>
                          <pre className="bg-white border rounded-lg p-3 text-xs font-mono text-gray-700 overflow-x-auto">{JSON.stringify(ep.response_example, null, 2)}</pre>
                        </div>
                      )}
                      {ep.curl && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">cURL Example</p>
                          <div className="bg-gray-900 rounded-lg p-3 flex items-start gap-2">
                            <code className="text-xs text-green-300 font-mono flex-1 break-all">{ep.curl}</code>
                            <button onClick={() => copy(ep.curl)} className="text-gray-400 hover:text-white shrink-0"><Copy className="h-3.5 w-3.5" /></button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Webhook Events */}
        {docs.webhooks && (
          <section>
            <h2 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
              <Webhook className="h-5 w-5 text-orange-500" /> Webhook Events
            </h2>
            <p className="text-sm text-gray-600 mb-4">{docs.webhooks.description}</p>

            <div className="bg-white border rounded-2xl p-5 mb-4">
              <p className="text-xs font-semibold text-gray-700 mb-2">Delivery Details</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                <p><span className="font-medium">Method:</span> {docs.webhooks.delivery?.method}</p>
                <p><span className="font-medium">Content-Type:</span> {docs.webhooks.delivery?.content_type}</p>
                <p><span className="font-medium">Timeout:</span> {docs.webhooks.delivery?.timeout}</p>
                <p><span className="font-medium">Retries:</span> {docs.webhooks.delivery?.retries}</p>
              </div>
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-600 mb-1">Custom Headers Sent:</p>
                {docs.webhooks.delivery?.headers && Object.entries(docs.webhooks.delivery.headers).map(([k, v]) => (
                  <div key={k} className="flex gap-2 text-xs text-gray-600">
                    <code className="font-mono text-indigo-600">{k}</code>
                    <span>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              {(docs.webhooks.events || []).map((ev, i) => {
                const isExpanded = expanded === `wh_${i}`;
                return (
                  <div key={i} className="bg-white border rounded-xl overflow-hidden">
                    <button onClick={() => setExpanded(isExpanded ? null : `wh_${i}`)}
                      className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-gray-50 transition-colors">
                      <span className="text-[10px] font-bold px-2.5 py-0.5 rounded bg-orange-100 text-orange-700 shrink-0">EVENT</span>
                      <code className="text-sm font-mono text-gray-900 flex-1">{ev.event}</code>
                      {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                    </button>
                    {isExpanded && (
                      <div className="border-t px-5 py-4 bg-gray-50/50 space-y-3">
                        <p className="text-sm text-gray-700">{ev.description}</p>
                        {ev.next_step && <p className="text-xs text-indigo-600 italic font-medium">{ev.next_step}</p>}
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">Payload Example</p>
                          <pre className="bg-white border rounded-lg p-3 text-xs font-mono text-gray-700 overflow-x-auto">{JSON.stringify(ev.payload_example, null, 2)}</pre>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Setup Steps */}
            {docs.webhooks.setup_steps && (
              <div className="mt-4 bg-white border rounded-2xl p-5">
                <p className="text-xs font-semibold text-gray-700 mb-2">How to Set Up Webhooks</p>
                {docs.webhooks.setup_steps.map((step, i) => (
                  <p key={i} className="text-xs text-gray-600 leading-relaxed">{step}</p>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Footer */}
        <footer className="text-center py-8 border-t">
          <p className="text-xs text-gray-400">HireFast AI by TekWissen — Confidential API Documentation</p>
          <p className="text-xs text-gray-400 mt-1">Need an API key? Contact your HireFast Admin or email hirefast@tekwissen.in</p>
        </footer>
      </main>
    </div>
  );
}
